import { Component, OnInit, Injector, HostListener, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { WorkOrdersService } from '../../work-orders.service';
import { BodyWorkOrderService } from '../../../ve.service';
import { CgpConfirmationDialogService } from '../../../../../shared/cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { BodyNotificationDialogComponent } from '../dialogs/body-notification-dialog/body-notification-dialog.component';
import { Validators } from '@angular/forms';
import { WoAssignFormComponent } from '../../forms/wo-assign-form/wo-assign-form.component';
import { AuthenticationService } from '../../../../../auth.service';

@Component({
  selector: 'body-wo-edit',
  templateUrl: './body-wo-edit.component.html',
  styleUrls: ['../../work-orders.component.scss', '../body-wo-add/body-wo-add.component.scss']
})
export class BodyWoEditComponent implements OnInit, OnDestroy {
  appCardTitle: string;
  router: Router;
  service: WorkOrdersService;
  bodyWorkOrderService: BodyWorkOrderService;
  cgpConfirmationDialogService: CgpConfirmationDialogService;
  authService: AuthenticationService;
  isCancel = false;
  matDialog: MatDialog;

  constructor(injector: Injector) {
    this.service = injector.get(WorkOrdersService);
    this.bodyWorkOrderService = injector.get(BodyWorkOrderService);
    this.router = injector.get(Router);
    this.cgpConfirmationDialogService = injector.get(CgpConfirmationDialogService);
    this.matDialog = injector.get(MatDialog);
    this.authService = injector.get(AuthenticationService);
  }

  ngOnInit() {
    const workOrder = this.service.bodyMainEditForm.value;
    this.setPageTitle(workOrder.WorkOrderNumber);
  }

  ngOnDestroy() {
    console.log('ngOnDestroy');
    this.service.resetForm();
    this.service.enableForm();
    this.service.resetGridView();
  }

  setPageTitle(workOrderNumber: any) {
    let action;
    if (this.service.isViewOnlyWO) {
      action = '';
    } else if (this.service.isCopyWO) {
      action = 'Copy ';
    } else {
      action = 'Edit ';
    }
    this.appCardTitle = `${action}Body Work Order: ` + workOrderNumber;
  }

  saveOrSubmit(event: any) {
    const action = event.currentTarget.id;
    const workOrder = this.service.bodyMainEditForm.value;
    let draftStatusId: any;

    this.bodyWorkOrderService.read('/GetWorkOrderStatuses')
      .then(response => {
        response.forEach(element => {
          if (element.Description.toLowerCase() === 'draft') {
            draftStatusId = element.Id;
          }
        });

        if (action.toLowerCase() === 'save' && workOrder.StatusId !== draftStatusId) {
          this.notifyUsers(workOrder);
        } else {
          this.service.saveOrSubmit(action, workOrder);
        }
      });
  }

  back = () => {
    if (this.service.isViewOnlyWO) {
      this.router.navigate([this.service.workOrderGridRouting[this.service.getWorkOrderGridType()]()]);
    } else {
      const workOrder = this.service.bodyMainEditForm.value;
      this.service.back(workOrder, false);
    }
  }

  cancel() {
    const workOrder = this.service.bodyMainEditForm.value;
    this.service.back(workOrder, true);
  }

  public get isViewOnly(): boolean {
    return this.service.isViewOnlyWO;
  }

  public get showSubmit(): boolean {
    return this.service.isCopyWO || this.service.isNewWO
      || this.service.woStatus.toLowerCase() === 'draft';
  }

  public get isFormValid(): boolean {
    return this.service.bodyMainEditForm.valid;
  }

  notifyUsers(workOrder: any) {
    const data = {
      title: 'Notify Contacts of Changes',
      yesLabel: 'Submit',
      noLabel: 'Cancel',
      contactsDescription: 'Contacts to Notify:',
      messageDescription: 'Message:',
      statusId: workOrder.StatusId,
    };

    const dialogRef = this.matDialog.open(BodyNotificationDialogComponent, { data });

    dialogRef.afterClosed()
      .subscribe(result => {
        if (result) {
          this.bodyWorkOrderService.loading.next(true);
          if (!result.NotificationList) {
            result.NotificationList = [];
          }
          if (!result.EmailNotes) {
            result.EmailNotes = '';
          }
          const body = { ...workOrder, ...result };

          return this.bodyWorkOrderService.save(body, false, `/Save/${workOrder.Id}`)
            .then(response => {
              this.bodyWorkOrderService.loading.next(false);
              this.service.displaySnackbar(response, 'save');
            });
        }
      });
  }

  /**
   * Determines if a user can assign the work order to an engineer.
   *
   * @readonly
   * @type {boolean}
   * @memberof BodyWoEditComponent
   */
  public get showAssign(): boolean {
    const user = this.authService.getUser();
    const isUnAssigned = this.service.woStatus === 'UnAssigned';
    const isAdmin = this.isUserInRole(user, 'Administrator');
    const isVeLead = this.isUserInRole(user, 'Lead');
    return isUnAssigned && (isAdmin || isVeLead);
  }

  /**
   * Assigns the current work order to an engineer.
   * VE Admins and Leads can assign work orders from this if the work order is in un-assigned status.
   *
   * @memberof BodyWoEditComponent
   */
  AssignNewWorkOrder() {
    const dialog = WoAssignFormComponent;
    const data = {
      title: `Schedule Work Order`,
      yesLabel: 'Submit',
      noLabel: 'Cancel',
      requiredDate: this.service.bodyMainEditForm.get('RequiredDate').value,
      workOrderId: this.service.bodyMainEditForm.get('Id').value,
    };

    const dialogRef = this.matDialog.open(dialog, { data });

    dialogRef.afterClosed()
      .subscribe(response => {
        if (response) {
          this.router.navigate([this.service.workOrderGridRouting[this.service.getWorkOrderGridType()]()]);
        }
      });
  }

  /**
     * Determines if the user is only a VE_User.
     *
     * @param {*} user
     * @param {*} role
     * @returns
     * @memberof DataGridComponent
     */
  isUserInRole(user, role) {
    const isVERole = user.RoleClaims.filter(roleClaim => {
      const userRoleClaimsContents = roleClaim.split('_');
      const userRoleClaimsModule = userRoleClaimsContents[0];
      const userRoleClaimsRole = userRoleClaimsContents[2];
      const currentUserModule = this.authService.getModule().split('_');
      if (userRoleClaimsRole && userRoleClaimsRole.includes(role) && currentUserModule[1].toLowerCase() === userRoleClaimsModule.toLowerCase()) {
        return roleClaim;
      }
    });
    return isVERole.length !== 0;
  }
}
